import React, { useState, useEffect, useCallback } from "react";
import { Input, Table, Select, Button } from "antd";
import Menubar from "../../../components/WhrManager/App";
import { http } from "../../../services/http";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Navbar from "../../../components/navbar";
import { useSelector } from "react-redux";
import ViewAvailabilityReports from "../../../components/ViewAvailabiltyReport/index";
import { debouncedSet } from "../../../utils/debounce";

const AvailabilityReport = () => {
  const { t } = useTranslation();
  const { warehouse } = useSelector((state) => state.main) || "";

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);

  const [itemGroup, setItemGroup] = useState("");
  const [typeGroup, setTypeGroup] = useState("");
  const [subGroup, setSubGroup] = useState("");
  const [itemName, setItemName] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [pageSize] = useState(30);
  const [whsCode, setWhsCode] = useState(warehouse);
  const [whsData, setWhsData] = useState([]);
  const [subGroupData, setSubGroupData] = useState([]);
  const [typeGroupData, setTypeGroupData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [itmGrpData, setItmGrpData] = useState([]);

  const handleNextPage = () => {
    if (hasMoreData) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      let url = `api/dashboard/availability-report?itemGroup=${itemGroup}&typeGroup=${typeGroup}&subGroup=${subGroup}&itemName=${itemName}&skip=${currentPage}&pageSize=${pageSize}`;

      const { data } = await http.get(url);

      setHasMoreData(data.length === pageSize);

      setDataSource(data);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  }, [itemGroup, typeGroup, subGroup, itemName, currentPage]);

  const fetchWarehouse = useCallback(async () => {
    try {
      const { data } = await http.get(`api/warehouses`);

      setWhsData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchItemGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/item-groups`);

      setItmGrpData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchSubGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/user-defined-fields`, {
        params: {
          FieldId: 1,
          TableID: "OITM",
        },
      });

      setSubGroupData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchTypeGroups = useCallback(async () => {
    try {
      const { data } = await http.get(`api/items/user-defined-fields`, {
        params: {
          FieldId: 0,
          TableID: "OITM",
        },
      });

      setTypeGroupData(data);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchWarehouse();
    fetchSubGroups();
    fetchTypeGroups();
    fetchItemGroups();
  }, [fetchWarehouse, fetchSubGroups, fetchTypeGroups, fetchItemGroups]);

  const renderWhs = (whs) => {
    return {
      title: dataSource[0]?.[`${whs}WarehouseName`] || "",
      dataIndex: "", // parent column doesn't need dataIndex
      key: `${whs}Group`, // unique key for the parent
      children: [
        {
          title: t("available"),
          dataIndex: `${whs}Available`,
          key: `${whs}Available`,
          align: "right",
          render: (text, record) => {
            const value = record[`${whs}Available`];
            return value != null
              ? new Intl.NumberFormat("fr-FR").format(value)
              : "-";
          },
        },
        {
          title: t("committed"),
          dataIndex: `${whs}Committed`,
          key: `${whs}Committed`,
          align: "right",
          render: (text, record) => {
            const value = record[`${whs}Committed`];
            return value != null
              ? new Intl.NumberFormat("fr-FR").format(value)
              : "-";
          },
        },
        {
          title: t("onHand"),
          dataIndex: `${whs}OnHand`,
          key: `${whs}OnHand`,
          align: "right",
          render: (text, record) => {
            const value = record[`${whs}OnHand`];
            return value != null
              ? new Intl.NumberFormat("fr-FR").format(value)
              : "-";
          },
        },
        {
          title: t("onOrder"),
          dataIndex: `${whs}OnOrder`,
          key: `${whs}OnOrder`,
          align: "right",
          render: (text, record) => {
            const value = record[`${whs}OnOrder`];
            return value != null
              ? new Intl.NumberFormat("fr-FR").format(value)
              : "-";
          },
        },
      ],
    };
  };

  const columns = [
    {
      title: t("itemName"),
      dataIndex: "itemName",
      key: "itemName",
      width: 200,
      fixed: "left",
    },
    {
      title: t("itemCode"),
      dataIndex: "itemCode",
      key: "itemCode",
    },
    {
      title: t("invntryUom"),
      dataIndex: "inventoryUom",
      key: "inventoryUom",
    },
    {
      title: t("itmsGrpNam"),
      dataIndex: "itemGroup",
      key: "itemGroup",
    },
    {
      title: t("u_typegroup"),
      dataIndex: "typeGroupUdf",
      key: "typeGroupUdf",
    },
    {
      title: t("u_subgroup"),
      dataIndex: "subGroupUdf",
      key: "subGroupUdf",
    },

    {
      title: t("lastPurchasePrice"),
      dataIndex: "lastPurchasePrice",
      key: "lastPurchasePrice",
      render: (text) => {
        return new Intl.NumberFormat("fr-FR").format(text) || "-";
      },
    },
  ];
  if (whsCode) {
    // Single warehouse
    columns.push(renderWhs(whsCode.toLowerCase()));
  } else {
    // Multiple warehouses
    columns.push(
      ...[
        {
          title: t("totalAvailable"),
          dataIndex: "totalAvailable",
          key: "totalAvailable",
          render: (text) => {
            return new Intl.NumberFormat("fr-FR").format(text) || "-";
          },
        },
        {
          title: t("totalCommitted"),
          dataIndex: "totalCommitted",
          key: "totalCommitted",
          render: (text) => {
            return new Intl.NumberFormat("fr-FR").format(text) || "-";
          },
        },

        {
          title: t("totalOnHand"),
          dataIndex: "totalOnHand",
          key: "totalOnHand",
          render: (text) => {
            return new Intl.NumberFormat("fr-FR").format(text) || "-";
          },
        },
        {
          title: t("totalOnOrder"),
          dataIndex: "totalOnOrder",
          key: "totalOnOrder",
          render: (text) => {
            return new Intl.NumberFormat("fr-FR").format(text) || "-";
          },
        },
        renderWhs("a1"),
        renderWhs("m1"),
        renderWhs("ms1"),
        renderWhs("s2"),
      ],
    );
  }

  const handleFilterChange = (value, setState) => {
    setCurrentPage(0);
    setState(value);
  };
  const handleFilterChangeDebounced = (value, setState) => {
    setCurrentPage(0);
    debouncedSet(value, setState);
  };

  const handleExportExcel = async () => {
    setExcelLoading(true);
    let backUrl = `api/dashboard/availability-report/excel`;
    const body = {
      itemGroup,
      typeGroup,
      subGroup,
      itemName,
      skip: 0,
      pageSize: 10000,
    };

    try {
      const res = await http.post(backUrl, body, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob", // Ensure the response is treated as a blob
      });

      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/json",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Mavjudlik hisoboti.xlsx"); // Define the download file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {}
    setExcelLoading(false);
  };

  return (
    <div className="flex w-full">
      <Menubar />
      <div className="h-screen w-full overflow-y-auto">
        <div className="h-screen w-full overflow-y-auto">
          <Navbar />
          <h1 className="font-poppins ml-4 mt-10 text-xl font-bold text-black sm:ml-5 sm:mt-14 sm:text-2xl">
            {t("availabilityReport")}
          </h1>
          <div className="mt-10 w-full border-[1px] border-[#E8E8E8] sm:mt-14"></div>

          <div className="mt-6 flex w-full flex-wrap items-start gap-4 px-4 sm:mt-10 sm:gap-7 sm:px-10">
            <div className="flex flex-col gap-3">
              <p>{t("itemName")}</p>
              <Input
                placeholder={t("itemName")}
                allowClear
                onChange={(e) =>
                  handleFilterChangeDebounced(e.target.value || "", setItemName)
                }
              />
            </div>

            <div className="flex flex-col gap-3">
              <p>{t("whs")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={whsCode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setWhsCode)
                }
              >
                {whsData.map((val) => (
                  <Select.Option key={val} value={val.warehouseCode}>
                    {val.warehouseName}
                  </Select.Option>
                ))}
              </Select>
              {/* {!whsCode && (
                <p className="text-red-500">{t("pleaseSelectWarehouse")}</p>
              )} */}
            </div>

            <div className="flex flex-col gap-3">
              <p>{t("itmsGrpNam")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={itemGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setItemGroup)
                }
              >
                {itmGrpData.map((val) => (
                  <Select.Option key={val} value={val.itmsGrpCod}>
                    {val.itmsGrpNam}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("u_typegroup")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={typeGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setTypeGroup)
                }
              >
                {typeGroupData.map((val) => (
                  <Select.Option key={val} value={val.fldValue}>
                    {val.descr}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-3">
              <p>{t("u_subgroup")}</p>
              <Select
                allowClear
                style={{ width: 200 }}
                value={subGroup}
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                showSearch
                onChange={(value) =>
                  handleFilterChange(value || "", setSubGroup)
                }
              >
                {subGroupData.map((val) => (
                  <Select.Option key={val} value={val.fldValue}>
                    {val.descr}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="mb-4 flex flex-col justify-between px-4 sm:mt-10 sm:flex-row sm:gap-7">
            <div className="font-nunitto font-bold">
              {t("page")} : {currentPage + 1}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 0}
                className="h-[40px] w-[120px] rounded-2xl bg-gray-300 text-gray-700 disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("previous")}
              </button>
              <button
                onClick={handleNextPage}
                disabled={!hasMoreData}
                className="h-[40px] w-[120px] rounded-2xl bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400 sm:w-[120px]"
              >
                {t("next")}
              </button>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-end px-4">
            <Button
              onClick={handleExportExcel}
              loading={excelLoading}
              type="primary"
              className="ml-5 h-10 w-full rounded-lg bg-green-700 text-white sm:w-[200px]"
            >
              {t("exportExcel")}
            </Button>
          </div>
          <div className="mb-4 px-4 sm:mt-10 sm:flex-row sm:gap-7 sm:px-4">
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
              className="mt-6 w-full sm:mt-10"
              rowKey="zakaz"
              sticky={true}
              scroll={{ x: whsCode ? 1500 : 3500 }}
              loading={loading}
              bordered
              onRow={(record) => {
                return {
                  onClick: () => {
                    setSelectedRow(record);
                    setIsModalVisible(true);
                  },
                };
              }}
            />
          </div>
        </div>
        <ViewAvailabilityReports
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
          }}
          data={selectedRow}
          filters={{
            whsCode,
          }}
        />
      </div>
    </div>
  );
};

export default AvailabilityReport;
